import * as React from "react";
import { Grid2 as Grid } from "@mui/material";
import { ChangeHandler } from "@/lib/modelstate";
import { ErrorsShape } from "@/lib/utils";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import { CardEx } from "@/lib/components/CardEx";
import { languageOptions, salutationOptions, useAvailableLanguages } from "@/datacaches";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import { CompanySignUpDTO } from "@/api/data-contracts";
import InfoTooltipIcon from "@/lib/components/InfoTooltipIcon";
import { Constants } from "@/api/constants";
import { useTranslation } from "react-i18next";
import { CommonI18N, UserI18N } from "@/translations";
import { toLanguageSelectOptions } from "@/pages/user-page/languageSelectOptions";

export const UserSignUpForm = ({
    model,
    errors,
    onChange
}: {
    model?: CompanySignUpDTO;
    onChange: ChangeHandler;
    errors: ErrorsShape<CompanySignUpDTO>;
}) => {
    const { t } = useTranslation();

    const salutationSelectOptions = salutationOptions.map((option) => ({
        label: option && t(CommonI18N.enum.salutation[option]),
        value: option
    }));

    const [availableLanguages] = useAvailableLanguages();
    // While `useAvailableLanguages()` is loading (or permanently, on a fetch error) it resolves
    // to `[]` — fall back to the static baseline so the dropdown isn't empty (same fallback the
    // sign-up schema uses, so dropdown and validation stay aligned).
    const effectiveLanguages = availableLanguages.length > 0 ? availableLanguages : languageOptions;
    const languageSelectOptions = toLanguageSelectOptions(effectiveLanguages, t);

    return (
        <CardEx title={t(UserI18N.form.page.title)}>
            <Grid container spacing={3} direction="column" width="100%">
                <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                    <StringInputField
                        name="email"
                        value={model?.email}
                        label={t(UserI18N.form.field.email)}
                        error={errors?.email}
                        maxChars={Constants.EMAIL_LENGTH_MAX}
                        endAdornment={<InfoTooltipIcon tooltipText={t(UserI18N.form.tooltip.emailCannotBeChanged)} />}
                        disabled
                    />
                </Grid>
                <Grid container direction="row" spacing={2} width="100%">
                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="password"
                            type="password"
                            label={t(UserI18N.form.field.password)}
                            value={model?.password}
                            error={errors?.password}
                            maxChars={Constants.PASSWORD_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>

                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="confirmPassword"
                            type="password"
                            label={t(UserI18N.form.field.confirmPassword)}
                            value={model?.confirmPassword}
                            error={errors?.confirmPassword}
                            maxChars={Constants.PASSWORD_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 4, md: 2, lg: 1 }}>
                    <SelectInputField
                        name="salutation"
                        label={t(UserI18N.form.field.salutation)}
                        value={model?.salutation}
                        error={errors?.salutation}
                        options={salutationSelectOptions}
                        displayEmpty={true}
                        onChange={onChange}
                    />
                </Grid>
                <Grid container direction="row" spacing={2} width="100%">
                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="firstName"
                            label={t(UserI18N.form.field.firstName)}
                            value={model?.firstName}
                            error={errors?.firstName}
                            maxChars={Constants.FIRSTNAME_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="lastName"
                            label={t(UserI18N.form.field.lastName)}
                            value={model?.lastName}
                            error={errors?.lastName}
                            maxChars={Constants.LASTNAME_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid container direction="row" spacing={2} width="100%">
                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="phone"
                            label={t(UserI18N.form.field.phone)}
                            value={model?.phone}
                            error={errors?.phone}
                            maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                        <StringInputField
                            name="fax"
                            label={t(UserI18N.form.field.fax)}
                            value={model?.fax}
                            error={errors?.fax}
                            maxChars={Constants.PHONE_NUMBER_LENGTH_MAX}
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
                <Grid size={{ sm: 4, md: 3, lg: 2 }}>
                    <SelectInputField
                        name="language"
                        label={t(UserI18N.form.field.language)}
                        value={model?.language}
                        error={errors?.language}
                        options={languageSelectOptions}
                        displayEmpty={true}
                        onChange={onChange}
                    />
                </Grid>
                <Grid size={{ sm: 6, md: 4, lg: 3 }}>
                    <CheckboxInputField
                        name="ceo"
                        label={t(UserI18N.form.field.isCEO)}
                        onChange={onChange}
                        value={model?.ceo}
                        error={errors?.ceo}
                    />
                </Grid>
            </Grid>
        </CardEx>
    );
};
